import { AcceptTermsCheckbox, AsyncActionButton, ExceptionErrorCode, getFirstOriginationFromStorage, isEquivalentToSavedOrigination, reportErrorToServer, ScrollableAlert } from '@applyonline/shared'
import { EProvince } from '@if/core/types'
import { ValidationError } from '@if/core/yup'
import { AxiosError } from 'axios'
import { useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { SubmitCreditApplicationDto, useSubmitCreditApplication } from '../../../api/credit-api'
import { useAppStore } from '../../../data/AppContext'
import { getRecaptchaToken } from '../../../services/recaptcha'
import { buildCreditApplicationSchemaObject, CreditApplication } from '../../../types/Schemas/CreditApplicationSchema'
import ReviewApplicantInfo from './ReviewApplicantInfo'

interface Props {
  gotoStep: (step: string) => void
  creditApplication: CreditApplication
}

const ReviewCreditApplication = ({ creditApplication, gotoStep }: Props) => {
  const { t } = useTranslation()
  const { executeRecaptcha } = useGoogleReCaptcha()

  const navigate = useNavigate()

  const [hasDuplicateLoanRequest, setHasDuplicateLoanRequest] = useState(false)
  const [submitCreditApplication, isSubmitting, isSuccess] = useSubmitCreditApplication()
  const [hasApplicantError, setHasApplicantError] = useState<boolean>(false)
  const [hasApplicantAddressError, setHasApplicantAddressError] = useState<boolean>(false)
  const [hasApplicantRevenueError, setHasApplicantRevenueError] = useState<boolean>(false)
  const [hasFormError, setHasFormError] = useState<boolean>(false)
  const [isHandlerRunning, setIsHandlerRunning] = useState(false)
  const appStore = useAppStore()
  const { applicant } = creditApplication

  const shouldDisplayReportMessage =
    applicant.currentAddress.stateIso === EProvince.Quebec.toString() && !creditApplication.isSinglePayment

  const creditApplicationDataContainsErrors = async () => {
    setHasApplicantError(false)
    setHasApplicantAddressError(false)
    setHasApplicantRevenueError(false)
    setHasFormError(false)
    let formHasError = false
    try {
      await buildCreditApplicationSchemaObject().validate(creditApplication, {
        abortEarly: false,
      })
    } catch (error) {
      if (error instanceof ValidationError) {
        error.inner.forEach((err) => {
          const path = err.path!
          if (path.includes('applicant')) {
            setHasApplicantError(true)
            formHasError = true
          }
          if (path.includes('applicant.currentAddress')) {
            setHasApplicantAddressError(true)
            formHasError = true
          }
          if (path.includes('applicant.currentJobs')) {
            setHasApplicantRevenueError(true)
            formHasError = true
          }
        })
      }
    }
    if (formHasError) {
      setHasFormError(formHasError)
      window.scrollTo(0, 0)
    }

    return formHasError
  }

  const buildDtoAndSubmitPrequal = async (evt: { preventDefault: () => void }) => {
    evt.preventDefault()
    if (isHandlerRunning) return
    setIsHandlerRunning(true)
    try {
      let firstOrigination = getFirstOriginationFromStorage();
      let originationFromUrl = appStore.originationFromUrl;

      if (originationFromUrl == null || isEquivalentToSavedOrigination(originationFromUrl))
      {
        originationFromUrl = firstOrigination;
        firstOrigination = null;
      }

      if ((await creditApplicationDataContainsErrors()) === false) {
        try {
          const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_postCreditApplicationAndPrequalify')
          const dto: SubmitCreditApplicationDto = {
            recaptchaToken,
            creditApplication: {
              ...creditApplication,
              lastTouchOrigination: originationFromUrl,
              firstTouchOrigination: firstOrigination,
            },
          }
          const creditApp = await submitCreditApplication(dto)
          navigate(`/creditApplication/${creditApp.id}`, { replace: true })
        } catch (error) {
          const axiosError = error as AxiosError<{ errorCode: string }>
          const errorCode = axiosError.response?.data?.errorCode
          if (axiosError.response?.status === 422 && errorCode === ExceptionErrorCode.RecaptchaException) {
            navigate('/recaptcha-validation-failed')
          } else if (errorCode === ExceptionErrorCode.DuplicateException) {
            setHasDuplicateLoanRequest(true)
          } else {
            reportErrorToServer(error as Error)
          }
        }
      }
    } finally {
      setIsHandlerRunning(false)
    }
  }

  return (
    <section className="step-content">
      <h3 className="form-question">{t('review.title')}</h3>
      <div className="paragraph">
        <p>{t('review.description')}</p>
      </div>

      <form onSubmit={buildDtoAndSubmitPrequal}>
        <ScrollableAlert showAlert={hasFormError} type="error" message={t('review.formContainsErrors')} />
        <ScrollableAlert
          showAlert={hasDuplicateLoanRequest}
          type="warn"
          message={t('contact.hasActiveLoanRequest')}
          link={t('contact.icebergPhone')}
          linkText={t('contact.icebergPhone')}
          linkType="tel"
        />

        <div className="review-wrap">
          <ReviewApplicantInfo
            applicant={applicant}
            hasApplicantError={hasApplicantError}
            hasApplicantAddressError={hasApplicantAddressError}
            hasApplicantRevenueError={hasApplicantRevenueError}
            gotoStep={gotoStep}
          />
        </div>

        <footer className="review-footer">
          {shouldDisplayReportMessage ? (
            <h3>
              <Trans i18nKey="confirmation.consentTitle" />
            </h3>
          ) : (
            <h3>
              <Trans i18nKey="confirmation.consentTitleNoCreditReport" />
            </h3>
          )}

          <AcceptTermsCheckbox
            msgOnInvalid={t('common.acceptConditions')}
            id="acceptConditions"
            termId={shouldDisplayReportMessage ? t('confirmation.consent') : t('confirmation.consentNoCreditReport')}
          />

          <AcceptTermsCheckbox
            msgOnInvalid={t('common.acceptConditions')}
            id="acceptAuthorise"
            termId={`${shouldDisplayReportMessage ? t('common.acceptAuthorization') : ''}`}
          >
            {shouldDisplayReportMessage ? <br /> : null}
            {t('confirmation.authorization')}
            <u>
              <a href={t('common.policyLink')} target="_blank" rel="noreferrer">
                {t('contact.linkText')}
              </a>
            </u>
            {t('confirmation.authorizationEnd')}
          </AcceptTermsCheckbox>

          <AsyncActionButton
            type="submit"
            className="btn btn-blue"
            disabled={isHandlerRunning || isSubmitting || hasFormError}
            isPending={isHandlerRunning || isSubmitting || isSuccess}
            primary
          >
            <span>{t('review.getQuote')}</span>
            <i className="fa-regular fa-arrow-right" />
          </AsyncActionButton>

          {shouldDisplayReportMessage && (
            <div style={{ borderTop: '1px solid black', marginTop: '5rem' }}>
              <span style={{ fontSize: '1.25rem', lineHeight: '1.45rem' }}>
                <p style={{ marginTop: '2rem' }}>
                  <span>¹</span>
                  {t('prequalification.priorToQualifyNotes.creditReport')}
                </p>
              </span>
            </div>
          )}
        </footer>
      </form>
    </section>
  )
}

export default ReviewCreditApplication
